/* OBFUSCATED CODE - INTELLECTUAL PROPERTY PROTECTED */
'use client';
import { useCallback } from 'react';
import { calculateDynamicParams } from './sprayPresets';
import { resetPresetToDefaults } from './presetResetManager';
export function useSprayControls({
    presets,
    activePreset,
    setActivePreset,
    activeCanId,
    cans,
    currentColor,
    userOverrides,
    setUserOverrides,
    presetManager,
    paramSetters,
    paramRefs,
    isPreviewMode,
    previewColor,
    previewCanId,
}) {
    const applyPreset = useCallback((presetKey) => {
        if (!presets[presetKey]) {
            return;
        }
        const selectedPreset = presets[presetKey];
        const effectiveCanId = (isPreviewMode && previewCanId) ? previewCanId : activeCanId;
        const activeCan = cans.find(can => can.id === effectiveCanId);
        const paintLeft = activeCan ? activeCan.paintLeft : 10000;
        const dynamicParams = calculateDynamicParams(paintLeft, presetKey);
        const overrides = userOverrides[presetKey] || {};
        const finalRadius = overrides.radius ?? dynamicParams.radius;
        const finalDensity = overrides.density ?? dynamicParams.density;
        const finalSpeedFactor = overrides.speedFactor ?? dynamicParams.speedFactor;
        const finalScale = overrides.scale ?? selectedPreset.scaleVal;
        presetManager.registerDefaultValues(
            presetKey,
            selectedPreset.scaleVal,
            dynamicParams.radius,
            dynamicParams.density,
            dynamicParams.speedFactor
        );
        paramSetters.setScaleVal(finalScale);
        paramSetters.setRadiusVal(finalRadius);
        paramSetters.setDensityVal(finalDensity);
        paramSetters.setSpeedFactorVal(finalSpeedFactor);
        setActivePreset(presetKey);
        paramSetters.setActualRadius(finalRadius);
        paramSetters.setActualDensity(finalDensity);
        paramSetters.setActualSpeed(finalScale);
        paramSetters.setActualSpeedFactor(finalSpeedFactor);
        if (paramRefs.scaleValRef?.current)
            paramRefs.scaleValRef.current.textContent = finalScale.toFixed(2);
        if (paramRefs.radiusValRef?.current)
            paramRefs.radiusValRef.current.textContent = finalRadius;
        if (paramRefs.densityValRef?.current)
            paramRefs.densityValRef.current.textContent = finalDensity;
        if (paramRefs.speedFactorValRef?.current)
            paramRefs.speedFactorValRef.current.textContent = finalSpeedFactor.toFixed(1);
        let effectivePreset = selectedPreset;
        if (activeCan?.type === 'roller') {
            effectivePreset = { ...selectedPreset, enableDrips: false };
        }
        if (window.updateSprayParams) {
            window.updateSprayParams(
                finalRadius, finalDensity, finalScale, finalSpeedFactor,
                presetKey,
                effectivePreset.enableDrips || false,
                effectivePreset.paintConsumption || 1,
                effectivePreset,
                effectiveCanId, isPreviewMode && previewColor ? previewColor : currentColor, cans
            );
        }
    }, [presets, cans, activeCanId, currentColor, userOverrides, presetManager,
        setActivePreset, paramSetters, paramRefs, isPreviewMode, previewColor, previewCanId]);
    const handleResetPreset = useCallback(() => {
        const defaults = presetManager.getDefaultValues();
        resetPresetToDefaults(
            defaults,
            {
                setScaleVal: paramSetters.setScaleVal,
                setRadiusVal: paramSetters.setRadiusVal,
                setDensityVal: paramSetters.setDensityVal,
                setSpeedFactorVal: paramSetters.setSpeedFactorVal,
                setActualSpeed: paramSetters.setActualSpeed,
                setActualRadius: paramSetters.setActualRadius,
                setActualDensity: paramSetters.setActualDensity,
                setActualSpeedFactor: paramSetters.setActualSpeedFactor,
                setUserOverrides,
            },
            paramRefs,
            { activePreset, presets, activeCanId: isPreviewMode && previewCanId ? previewCanId : activeCanId, currentColor: isPreviewMode && previewColor ? previewColor : currentColor, cans },
            window.updateSprayParams,
            presetManager.clearChangesFlag
        );
    }, [presetManager, activePreset, presets, activeCanId, currentColor, cans,
        setUserOverrides, paramSetters, paramRefs, isPreviewMode, previewColor, previewCanId]);
    return { applyPreset, handleResetPreset };
}
